'use client'

import { useEffect, useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { History, LogOut, CreditCard, LayoutTemplate, Loader2 } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useAuth } from '@/contexts/auth-context'
import { HistoryModal } from '@/components/history/history-modal'
import { TemplateModal } from '@/components/templates/template-modal'
import { useRouter, usePathname } from 'next/navigation'
import { useLanguage } from '@/contexts/language-context'

export function UserAvatarMenu() {
  const { user, signOut, userSubscription, refreshUserSubscription } = useAuth()
  const router = useRouter()
  const pathname = usePathname()
  const [userInitial, setUserInitial] = useState('')
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false)
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const { t } = useLanguage()
  
  useEffect(() => {
    if (user?.email) {
      // 获取用户名首字母
      const initial = user.email.charAt(0).toUpperCase()
      setUserInitial(initial)
    } else if (user?.user_metadata?.full_name) {
      const initial = user.user_metadata.full_name.charAt(0).toUpperCase()
      setUserInitial(initial)
    } else if (user?.user_metadata?.name) {
      const initial = user.user_metadata.name.charAt(0).toUpperCase()
      setUserInitial(initial)
    } else {
      setUserInitial('U')
    }
  }, [user])

  const handleOpenChange = async (open: boolean) => {
    setMenuOpen(open)
    if (open) {
      setIsRefreshing(true)
      await refreshUserSubscription()
      setIsRefreshing(false)
    }
  }

  if (!user) return null

  const isFree = userSubscription?.plan === 'free'

  // Get plan display name
  const getPlanDisplayName = () => {
    if (!userSubscription) return t('pricing.planName.free')
    switch (userSubscription.plan) {
      case 'pro-monthly': return t('pricing.planName.proMonthly')
      case 'pro-yearly': return t('pricing.planName.proYearly')
      case 'max-monthly': return t('pricing.planName.maxMonthly')
      case 'max-yearly': return t('pricing.planName.maxYearly')
      default: return t('pricing.planName.free')
    }
  }

  // Handle smart anchor link navigation
  const handleAnchorClick = (href: string) => {
    if (href.startsWith('#')) {
      // 如果当前不在主页面，先跳转到主页面，然后滚动到锚点
      if (pathname !== '/') {
        // 跳转到主页面并带上锚点
        router.push(`/${href}`)
      } else {
        // 如果已经在主页面，直接滚动到锚点
        const element = document.querySelector(href)
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          })
        }
      }
    }
  }

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            className="rounded-full p-0 h-10 w-10 bg-blue-300 dark:bg-emerald-800 hover:bg-emerald-700 text-white"
          >
            <Avatar className="h-10 w-10 border-2 border-blue-500 dark:border-emerald-700">
              <AvatarFallback className="bg-blue-300 dark:bg-emerald-800 text-white">
                {userInitial}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>{t('userMenu.myAccount') || 'My Account'}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="flex items-center cursor-pointer"
            onClick={() => handleAnchorClick('#pricing')}
          >
            <CreditCard className="mr-2 h-4 w-4" />
            {isRefreshing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <span>{getPlanDisplayName()}</span>
            )}
          </DropdownMenuItem>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  className={'flex items-center cursor-pointer'}
                  onClick={() => {
                    setIsHistoryModalOpen(true)
                  }}
                >
                  <History className="mr-2 h-4 w-4" />
                  <span>{t('userMenu.history') || 'History'}</span>
                </DropdownMenuItem>
              </TooltipTrigger>
              {/* {isFree && (
                <TooltipContent>Only for subscribed users(Pro/Max)</TooltipContent>
              )} */}
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  className={`flex items-center ${isFree ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  onClick={() => {
                    if (!isFree) setIsTemplateModalOpen(true)
                  }}
                >
                  <LayoutTemplate className="mr-2 h-4 w-4" />
                  <span>{t('userMenu.templates') || 'Templates'}</span>
                </DropdownMenuItem>
              </TooltipTrigger>
              {/* {isFree && (
                <TooltipContent>Only for subscribed users(Pro/Max)</TooltipContent>
              )} */}
            </Tooltip>
          </TooltipProvider>
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            className="flex items-center cursor-pointer text-red-600 focus:text-red-600" 
            onClick={signOut}
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span>{t('userMenu.signOut') || 'Sign Out'}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
      />
      <TemplateModal
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
      />
    </>
  )
}
